import { useEffect, useState } from 'react';
import { Activity, CalendarClock, Shield, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { getAdminOverview } from '../lib/api';

const formatDateTime = (value, lang) => {
  if (!value) return lang === 'hr' ? 'Nema aktivnosti' : 'No activity';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(lang === 'hr' ? 'hr-HR' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function AdminDashboard({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getAdminOverview();
        setOverview(data);
      } catch (err) {
        console.error('Admin overview load failed:', err);
        setError(lang === 'hr' ? 'Admin podaci se nisu mogli učitati.' : 'Admin data could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    loadOverview();
  }, [lang]);

  const metricCards = [
    { key: 'total', label: lang === 'hr' ? 'Ukupno korisnika' : 'Total users', value: overview?.total_users ?? 0, icon: Users },
    { key: 'today', label: lang === 'hr' ? 'Aktivni danas' : 'Active today', value: overview?.active_today ?? 0, icon: Activity },
    { key: '7d', label: lang === 'hr' ? 'Aktivni 7 dana' : 'Active 7 days', value: overview?.active_7d ?? 0, icon: CalendarClock },
    { key: '30d', label: lang === 'hr' ? 'Aktivni 30 dana' : 'Active 30 days', value: overview?.active_30d ?? 0, icon: Shield },
  ];

  return (
    <Layout user={user}>
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10 space-y-6" data-testid="admin-dashboard-page">
        <div className="rounded-3xl border border-stone-200 bg-white/85 backdrop-blur-sm p-6 md:p-8 shadow-sm" data-testid="admin-dashboard-hero">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-stone-500" data-testid="admin-dashboard-badge">Admin</p>
              <h1 className="text-3xl md:text-4xl font-heading font-semibold text-stone-900" data-testid="admin-dashboard-title">
                {lang === 'hr' ? 'Pregled korisnika i aktivnosti' : 'User and activity overview'}
              </h1>
              <p className="mt-2 text-sm text-stone-600 max-w-2xl" data-testid="admin-dashboard-subtitle">
                {lang === 'hr'
                  ? 'Ovdje vidiš broj korisnika, koliko su aktivni i kad su zadnji put koristili aplikaciju.'
                  : 'See total users, activity levels and when they last used the app.'}
              </p>
            </div>
            <div className="rounded-2xl bg-stone-100 px-4 py-3 text-sm text-stone-700 border border-stone-200" data-testid="admin-dashboard-admin-email">
              {overview?.admin_email || user?.email}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="rounded-3xl border border-stone-200 bg-white p-10 text-center text-stone-600" data-testid="admin-dashboard-loading">
            {lang === 'hr' ? 'Učitavanje admin pregleda...' : 'Loading admin overview...'}
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-700" data-testid="admin-dashboard-error">
            {error}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {metricCards.map((card) => (
                <div key={card.key} className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`admin-metric-card-${card.key}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-stone-500" data-testid={`admin-metric-label-${card.key}`}>{card.label}</p>
                      <p className="mt-2 text-3xl font-semibold text-stone-900" data-testid={`admin-metric-value-${card.key}`}>{card.value}</p>
                    </div>
                    <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <card.icon className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-3xl border border-stone-200 bg-white shadow-sm overflow-hidden" data-testid="admin-users-table-card">
              <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-heading font-semibold text-stone-900" data-testid="admin-users-table-title">
                    {lang === 'hr' ? 'Korisnici i zadnja aktivnost' : 'Users and latest activity'}
                  </h2>
                  <p className="text-sm text-stone-500" data-testid="admin-users-table-subtitle">
                    {lang === 'hr' ? 'Lista korisnika sortirana po zadnjoj aktivnosti ili prijavi.' : 'Users sorted by latest activity or sign in.'}
                  </p>
                </div>
                <div className="text-sm text-stone-500" data-testid="admin-users-count-text">
                  {(overview?.users || []).length}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 uppercase tracking-wide text-xs">
                    <tr>
                      <th className="px-5 py-3 text-left">{lang === 'hr' ? 'Korisnik' : 'User'}</th>
                      <th className="px-5 py-3 text-left">{lang === 'hr' ? 'Zadnja aktivnost' : 'Last activity'}</th>
                      <th className="px-5 py-3 text-left">{lang === 'hr' ? 'Izvor' : 'Source'}</th>
                      <th className="px-5 py-3 text-left">{lang === 'hr' ? 'Prijava' : 'Sign in'}</th>
                      <th className="px-5 py-3 text-left">{lang === 'hr' ? 'Aktivnost 30d' : 'Activity 30d'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(overview?.users || []).map((item) => (
                      <tr key={item.id} className="border-t border-stone-100" data-testid={`admin-user-row-${item.id}`}>
                        <td className="px-5 py-4 align-top">
                          <div className="font-medium text-stone-900" data-testid={`admin-user-email-${item.id}`}>{item.email || '—'}</div>
                          <div className="text-xs text-stone-500 mt-1" data-testid={`admin-user-name-${item.id}`}>{item.name || '—'}</div>
                        </td>
                        <td className="px-5 py-4 text-stone-700" data-testid={`admin-user-last-activity-${item.id}`}>{formatDateTime(item.last_activity_at, lang)}</td>
                        <td className="px-5 py-4 text-stone-700" data-testid={`admin-user-activity-source-${item.id}`}>{item.last_activity_source || (lang === 'hr' ? 'Nema podataka' : 'No data')}</td>
                        <td className="px-5 py-4 text-stone-700" data-testid={`admin-user-last-signin-${item.id}`}>{formatDateTime(item.last_sign_in_at, lang)}</td>
                        <td className="px-5 py-4 text-stone-900 font-medium" data-testid={`admin-user-activity-30d-${item.id}`}>{item.activity_events_30d ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}